/**
 * 播放相关类型定义
 * 从 playApi.ts 中提取的 interface 类型
 */

export enum VideoType {
  VTUNKNOWN = 0,
  LONG = 1, // 长视频
  SHORT = 2, // 短视频
  LIVE = 3, // 直播
}

// 角标展示位置
export enum MarkLabelPosition {
  LowerLeft = 0, // 左下
  LowerRight = 1, // 右下
  UpperRight = 2, // 右上
  UpperLeft = 3, // 左上
  LowerCenter = 4, // 下中
}

// 角标类型
export enum MarkLabelType {
  MarkLabelPay = 0, // 付费角标
}

// 角标信息
export interface MarkLabel {
  text: string; // 角标文本
  position: MarkLabelPosition; // 角标位置
  feature_color: string; // 角标
  url: string; // 图片地址
  height: number;
  width: number;
}

// 角标信息
export interface MarkLabelInfo {
  type: MarkLabelType; // 角标类型
  mark_label: MarkLabel; // 角标
}

// 上报参数相关
export interface ReportData {
  report_key: string; // report key
  report_params: string; // report params
}

// 跳转相关
export interface Action {
  url: string; // 跳转scheme+url
  report_data: ReportData; // 上报参数
}

// tag标签
export interface TagLabel {
  text?: string; // 标签文本
  text_color?: string; // 文本颜色
  bg_color?: string; // 背景色
  filter_choice?: { [key: string]: string }; // 筛选器的选中项。key是筛选维度（行），value是这一行的选中项
  icon_url?: string; // 图片链接如：vip 4k
}

// 投票信息
export interface VoteInfo {
  support_count: string; // 当前电力值
  actor_id: string; // 助力明星的actorid
  group_id: string; // 助力明星的用户组
  action: string; // 点击跳转action
  button_content: string; // 按钮文本
}

// 演员信息
export interface ActorInfo {
  actor_name: string; // 用户名称
  actor_id: string; // 用户编号(可以为空)
  face_image_url: string; // 头像图片
  action: Action; // 点击跳转action
  vote_info: VoteInfo; // 道具
}

// Poster扩展信息
export interface PosterExInfo {
  pure_img_op_url: string; // 纯净图裁剪图 16:9 、9:16
  img_url: string; // 老图 （横图、竖图）
}

export interface Poster {
  main_title: string; // 主标题
  subtitle: string; // 副标题
  description: string; // 详情文本
  img_url: string; // 海报图片
  mark_label_list: MarkLabel[]; // 角标列表
  report_data: ReportData; // 上报参数
  action: Action; // 跳转action
  ad_key: string; // 广告位（App端没用，TV端？）
  poster_id: string; // TV端和众包H5筛选页用于跳转（App端没用）
  episode_updated_country: number; // 多国家剧集更新集数（App端没用，TV端使用）
  tag_label_list: TagLabel[]; // 标签列表
  cid: string; // cid
  pid: string; // pid
  vid: string; // vid
  img_main_color: string; // 图片主颜色（用于跟随轮播图变换背景颜色）
  tid: string; // 合集tid
  actor_list: ActorInfo[]; // 主演明星列表
  tid_type: string; // tid内容类型："1"合集；"2"片单
  url: string; // 跳转URL（内部跳转）
  url_outer: string; // 外部跳转链接（外部跳转）
  expose_limit: number; // 曝光次数限制（0表示不限制）
  bg_img_url: string; // 背景图
  logo_img_url: string; // logo文字图片
  title_hide: boolean; // 隐藏标题：590新增
  nid: string; // 人名ID：5100新增 nameID
  area_name: string; // 制片地区名
  off_shelf_status: boolean; // 下架状态 若值为true为下架 专辑、视频用
  mapping_vid: string; // 映射vid（vid列的配置映射vid）
  // 2024年06月05日CMS模块新增：跳转tabbar的指定频道
  tab_name: string; // TabBar名字：用于跳转指定tabbar的频道ID，取值：https://git.woa.com/video_app_international/libs/trpc_go_publib/blob/master/biz/wetvenums/app_channel_id.go#L15
  channel_id: string; // 频道ID
  channel_type: number; // 频道类型
  // 万能模块的播放量字段
  play_count: string; // 播放量
  art_title_img: string; // 艺术字标题图片
  horizontal_info: PosterExInfo; // 横版海报图
  vertical_info: PosterExInfo; // 竖版海报图
  episode_updated_text: string; // 更新集数展示文本
}

// 剧集列表下一页信息
export interface NextPageInfo {
  data_key: string;
  page_context: string;
  has_next_page: boolean;
}

// 追剧日历
export interface PlayCalender {
  url: string; // 跳转地址
  img_url: string; // 日历图片
}

export enum PlayListUiType {
  PlayListTypeText = 0,
  PlayListTypePoster = 1,
  PlayListTypeVerticalPoster = 2,
}

// 播放器播放模式,短剧类型为竖版可上下滑动样式
export enum PlayerModel {
  PlayerModelLong = 0, // 精品库类型专辑
  PlayerModelSkit = 1, // 短剧类型专辑
}

// 播放列表推广位信息
export interface PopularizationInfo {
  title: string;
  tag: string; // 右上角标签
  action: Action; // 跳转链接
  horizontal_poster: Poster; // 横版海报，续播提示时展示
  vertical_poster: Poster; // 竖版海报，播放列表展示
}

export interface SeasonInfo {
  title: string;
  cid: string;
  next_page_info: NextPageInfo;
  play_calender: PlayCalender;
  play_list_ui_type: PlayListUiType;
  detail_info: string; // 详细信息文本
  player_model: PlayerModel; // 播放模式
  popularization_info: PopularizationInfo; // 推广位信息
  publish_year: number; // 制片年份
  update_episode: number; // 视频更新到第几集
  full_episode_count: number; // 视频一共有多少集
  poster: Poster; // 海报信息
}

// 影片付费类型
export enum VideoPaymentType {
  Unknown = 0,
  Vod = 4, // 单片点播           （会员用券，非会员支持单点，用券）
  VodVipExmt = 5, // 单片点播 & 会员豁免 （会员免费看，非会员支持单点，用券）
  VipOnly = 6, // 仅会员可看         （会员专享，非会员必须开通会员）
  FreeForAll = 8, // 全部都免费
  SinglePay = 7, // 单片付费
  SinglePayPlus = 9, // 单片付费PLUS
  VodAhead = 12, // 超前点播
}

// 播放版权限制类型
export enum VideoCopyrightType {
  PlayInWebview = 0, // 表示要跳webview播放
  PlayInApp = 1, // 直接播放
  PlayInWebviewWithStmt = 101, // 跳webView播放并显示"应版权方要求不再app播放"提示
}

// ShareItem 分享
export interface ShareItem {
  share_url: string; // 分享url
  share_title: string; // 分享标题
  share_subtitle: string; // 分享副标题 （实际用于分享携带信息）
  share_img_url: string; // 分享图片
  vid: string; // VID
  cid: string; // CID
  share_vertical_img_url: string; // 竖图
  share_square_img_url: string; // 方图
  pid: string; // PID
  display_message: string; // 分享时携带的文案，展示为副标题
}

// VideoItemData 当前播视频数据
export interface VideoItemData {
  vid: string; // 视频id     ［如果是外站数据，请按照1，2，3 这样往后编号］
  payment_type: VideoPaymentType; // 影片付费类型（原：play_status）
  poster: Poster; // 用来负责ui显示 ［如果视频有海报图，均从这里取］
  skip_start: number; // 跳过片头时间（秒）
  skip_end: number; // 跳过片尾时间（秒）
  action: Action; // 点击当前剧集后执行的动作
  title: string; // 全屏显示的标题
  play_copyright_type: VideoCopyrightType; // 播放版本限制类型（原: play_copy_right）
  watch_record_poster: Poster; // 观看历史使用的视图信息
  share_data: ShareItem; // 分享item
  next_play_key: string; // 下一个播放的专辑
  try_watch_duration: number; // 试看时长单位：秒
  cid: string; // 视频对应cid
  pay_preview: boolean; // 付费预告片标识,若值为true,则会员用户需隐藏该vid
  stream_ratio: number; // 视频流宽高比
  not_record_history: boolean; // 指定该video不记录历史，默认是false
  disable_external_play: boolean; // 关闭投射功能，默认false
  episode_id: number; // 视频是第几集
  is_drm: boolean; // 是否是DRM视频
  publish_time: string; // 视频发布时间（秒）
  play_count: string; // 播放量
  duration: string; // 播放时长
  vid_type: number; // vid类型 1=长视频 2=短视频
  status: number; // 视频状态
  auto_play_type: number; // 自动播放类型：0.不自动播放；1.wifi自动播放；2.wifi和移动网络都自动播放
  age_limit: number; // 年龄限制 0就是不限制
  episode_id_text: string; // 显示使用的集数
  first_frame_poster: string; // 第一帧画面
  skip_head_begin: number; // 跳过片头的开始时间(秒),skip_start当作片头的结束时间点
  skip_tail_end: number; // 片尾的结束时间(秒), skip_end当作片尾的开始时间点
  is_trailer: boolean; // 是否是预告片
}

export interface VideoDetailBasicInfo {
  vid: string; // 启播的vid，有可能跟req传的不同
  cid: string; // 上面vid对应的cid
  video_type: number; // 视频类型，1是专辑，2是短视频
  primary_category: number; // 视频一级分类类型
  area_id: number; // 制片地区（和国家码数值定义一致）
  season_list: SeasonInfo[];
  current_video_data: VideoItemData;
  full_episode_count: number; // 视频一共有多少集
  update_episode: number; // 视频更新到第几集
  update_time: string; // 视频最新一集的更新时间（秒）
  online_time: string; // 上线时间 (时间戳)
  album_vertical_img_url: string; // 专辑竖图
  mark_label_info_list: MarkLabelInfo[]; // 待替换角标信息列表
  full_episode_count_text: string; // 视频一共有多少集文案
  show_credit_entry: boolean; // 是否展示积分入口
  show_score_entry: boolean; // 是否展示剧集分数入口
  content_score: number; // 剧集分数。如果小于0，则表示未开分
  poster: Poster; // cid维度海报、标题信息
  auto_play_video_data: VideoItemData; // 详情页自动播vid
}

export interface ExperimentInfo {
  experiment_id: string;
}

export interface WatchList {
  is_show: boolean; // 看单功能是否开启,默认关闭
  is_watched: boolean; // 是否已加入看单
}

export interface RecommendListInfo {
  next_page_info: NextPageInfo;
  recommend_list: Poster[];
}

export interface PlayListInfo {
  next_page_info: NextPageInfo;
  play_list_info: VideoItemData[];
}

// TV封面数据
export interface TVCoverData {
  cover_id: string; // cid
  desc: string; // 简介
  title: string; // 标题
  type: number; // 专辑类型
  ott_tags: MarkLabel[]; // TV角标
  square_tags: MarkLabel[]; // TV方标
  episode_updated: string; // 更新集数
  imgtag_ver: string; // 统一角标
  publish_date: string; // 上线日期
  second_title: string; // 副标题
  art_word: string; // 艺术字
  pic_url_vt: string; // 竖海报，普通图
  pure_pic_url_vt: string; // 竖海报，净版图
  pic_url_hz: string; // 横海报，普通图
  pure_pic_url_hz: string; // 横海报，净版图
}

export interface HistoryRecord {
  vid: string; // 单视频id
  cid: string; // 专辑id
  pid: string; // 直播id
  modify_time: string; // 最后修改时间（历史产生/更新/删除的时间）utc（毫秒）
  video_play_time: number; // 影片已经播放的时间，单位秒
  video_total_time: number; // 视频总时长，单位秒
  type: VideoType; // 视频类型
  primary_category: number; // 视频一级分类
  full_episode_count: number; // 这个cid的总集数
  update_episode: number; // 这个cid最新更新的集数
  update_time: string; // 这个cid最新更新的时间（秒）
  episode: number; // 这个vid的集数
  publish_time: string; // 这个vid的发布时间（秒）
  pay_status: number; // 媒资付费状态
  cover_title: string;
  cover_type: number; // 媒资专辑类型
  update_episode_multinational: string; // 这个cid最新更新的集数,优先2B形式，数字形式兜底
  watch_time: number; // 用户观看时长
  tv_cover_data?: TVCoverData; // TV封面数据
}

export interface PlayUrlResponse {
  ret?: number;
  code?: number;
  data?: {
    video_data: VideoDetailBasicInfo;
    experiment_info: ExperimentInfo[]; // A/B test信息
    watch_list: WatchList; // 看单
    recommend_list: RecommendListInfo; // 推荐视频
    play_list_info: PlayListInfo; // 播放列表
    related_video_info: PlayListInfo; // 花絮列表
  };
}

export interface PlayListResponse {
  ret?: number;
  code?: number;
  data?: {
    video_list: VideoItemData[]; // 视频列表
    next_page_info: NextPageInfo; // 下一页信息
  };
}

export interface PlayHistoryResponse {
  ret?: number;
  code?: number;
  data?: {
    record: HistoryRecord; // 历史记录
  };
}

export enum PlayListStyleType {
  SecondColumns = 'second_columns',
  ThirdColumns = 'third_columns',
}

export enum SeasonType {
  SeasonNormal = 'season_normal',
  SeasonRelatedvideo = 'season_relatedvideo',
}

export enum TagListType {
  TagListMain = 'tag_list_main',
  TagListSeriesListPage = 'tag_list_series_list_page',
}

export enum PlayDetailSeriesPageModule {
  SeasonList = 'play-detail-series-page-season-list',
  Rangelist = 'play-detail-series-page-rangelist',
  VideoList = 'play-detail-series-page-video-list',
  RetryBtn = 'play-detail-series-page-retry-btn',
}
